import { checkPermissions } from "@elit-hair/elit-tools";
import { ALL_LOCATIONS, ValidHotelType } from "@elit-hair/elitklinik-shared";
import { LocationOn, VideoCameraBack } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Easing, Tween } from "@tweenjs/tween.js";
import { AdvancedMarker, ControlPosition, MapControl, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useState } from "react";
import { TbCar, TbMapPin, TbPointFilled, TbPolygon } from "react-icons/tb";

import ElitHairIcon from "@/components/ElitHairIcon";
import FloatingActionButtons from "@/components/FloatingActionButtons";
import MapComponent from "@/components/Map";
import { PERMISSIONS } from "@/constants/permissions";
import { useCapacitor } from "@/hooks/useCapacitor";
import Navbar from "@/layout/Navbar";
import { SideMenuToggle } from "@/layout/SideMenu/components/SideMenuToggle";
import useDriversContext from "@/providers/data/DriversContext";
import useMapContext, { RouteType } from "@/providers/data/MapContext";
import useAuth from "@/providers/hooks/useAuth";
import usePopup from "@/providers/hooks/usePopup";
import { useGetTasks, useGetVehiclesQuery } from "@/requests/transport.queries";
import { DriverType } from "@/types/driver.types";
import { WeTrackVehicleData } from "@/types/wetrack.types";

import DriverCamera from "../Cameras/components/DriverCamera";
import { useMapZonesDrawer } from "./components/MapZonesDrawer";

const MapPage = () => {
    const { data: vehicles } = useGetVehiclesQuery(true);
    const { data: tasks } = useGetTasks({
        date: DateTime.now(),
    });

    const { isCordova } = useCapacitor();

    const [visibility, setVisibility] = useState({
        showLocations: true,
        showCars: true,
        showZones: true,
        showNames: false,
    });

    useMapZonesDrawer(visibility.showZones);

    const { mapLoaded, selectedVehicle, setSelectedVehicle } = useMapContext();

    const { showRoute, setShowRoute } = useMapContext();

    const { getDriverByVehicleId } = useDriversContext();

    const { userData } = useAuth();

    const { setPopup } = usePopup();

    useEffect(() => {
        if (!selectedVehicle) return;

        const vehicleId = selectedVehicle.properties.id.toString();
        const driver = getDriverByVehicleId(vehicleId);
        const task = tasks?.find((task) => task.driverId === driver?.id);

        if (task)
            setShowRoute({
                task,
                vehicle: selectedVehicle,
                driver: getDriverByVehicleId(vehicleId) || null,
            });
    }, [selectedVehicle, vehicles, getDriverByVehicleId, tasks, setShowRoute]);

    const [showingCameras, setShowingCameras] = useState<DriverType | null>();
    const handleOpenCamerasDialog = (selectedDriver: DriverType) => {
        setShowingCameras(selectedDriver);
    };
    useEffect(() => {
        if (!showingCameras) return;
        setPopup(
            <div className="pb-4">
                {showingCameras && (
                    <DriverCamera driver={showingCameras} licensePlate={showingCameras.vehicle?.licensePlate || ""} />
                )}
            </div>,
            {
                title: "Cameras",
                onClose: () => {
                    setShowingCameras(null);
                },
            }
        );
    }, [showingCameras, setPopup]);

    const map = useMap();
    const handleCenterVehicle = (vehicle: WeTrackVehicleData) => {
        if (!vehicle || !map) return;
        const [lng, lat] = vehicle.geometry.coordinates;
        // map.easeTo({ center: { lat, lng }, zoom: 15 });

        const cameraOptions: google.maps.CameraOptions = {
            tilt: map.getTilt(),
            heading: map.getHeading(),
            zoom: map.getZoom(),
            center: map.getCenter()?.toJSON(),
        };

        const tween = new Tween(cameraOptions)
            .to({ tilt: 65, heading: 0, zoom: 17, center: { lat, lng } }, 1000)
            .easing(Easing.Quadratic.Out)
            .onUpdate(() => {
                map.moveCamera(cameraOptions);
            })
            .start();

        map.moveCamera(cameraOptions);

        function animate(time: number) {
            tween.update(time);
            requestAnimationFrame(animate);
        }
        requestAnimationFrame(animate);
    };

    const handleGoToKlinik = () => {
        if (!map) return;
        const klinik = ALL_LOCATIONS?.find(
            (location): location is ValidHotelType =>
                location.isValidLocation && location.transportName === "Elit Klinik"
        );
        if (!klinik) return;
        const cameraOptions: google.maps.CameraOptions = {
            tilt: map.getTilt(),
            heading: map.getHeading(),
            zoom: map.getZoom(),
            center: map.getCenter()?.toJSON(),
        };

        const tween = new Tween(cameraOptions)
            .to({ tilt: 65, heading: 0, zoom: 17, center: klinik.coords }, 1000)
            .easing(Easing.Quadratic.Out)
            .onUpdate(() => {
                map.moveCamera(cameraOptions);
            })
            .start();

        map.moveCamera(cameraOptions);

        function animate(time: number) {
            tween.update(time);
            requestAnimationFrame(animate);
        }
        requestAnimationFrame(animate);
    };

    if (!mapLoaded) return <div>Loading...</div>;

    return (
        <div className="h-100%">
            <Navbar />
            <MapComponent>{showRoute ? <TaskRoute routeData={showRoute} /> : null}</MapComponent>

            <MapControl position={ControlPosition.TOP_RIGHT}>
                <div className={`flex gap-2 flex-col md:flex-row m-4 ${isCordova ? "mt-14" : ""}`}>
                    <Button
                        variant="contained"
                        color={visibility.showLocations ? "primary" : "info"}
                        onClick={() => {
                            setVisibility({ ...visibility, showLocations: !visibility.showLocations });
                        }}
                        size="small"
                    >
                        <TbMapPin className="text-xl" />
                    </Button>
                    {checkPermissions(userData?.permissionCodes, [PERMISSIONS.TR_SVC_MAP_ZONES_LIST]) && (
                        <Button
                            variant="contained"
                            color={visibility.showZones ? "primary" : "info"}
                            onClick={() => {
                                setVisibility({ ...visibility, showZones: !visibility.showZones });
                            }}
                            size="small"
                        >
                            <TbPolygon className="text-xl" />
                        </Button>
                    )}
                    {checkPermissions(userData?.permissionCodes, [PERMISSIONS.TR_SVC_VEHICLES_LIST]) && (
                        <Button
                            variant="contained"
                            color={visibility.showCars ? "primary" : "info"}
                            onClick={() => {
                                if (visibility.showCars && visibility.showNames) {
                                    setVisibility({
                                        ...visibility,
                                        showCars: !visibility.showCars,
                                        showNames: !visibility.showNames,
                                    });
                                } else {
                                    setVisibility({ ...visibility, showCars: !visibility.showCars });
                                }
                            }}
                            size="small"
                        >
                            <TbCar className="text-xl" />
                        </Button>
                    )}
                </div>
            </MapControl>

            <FloatingActionButtons className="md:hidden" position="left">
                <SideMenuToggle />
            </FloatingActionButtons>

            {visibility.showLocations &&
                ALL_LOCATIONS.filter((location) => location.isValidLocation).map((location) => {
                    let size = 40;
                    let zIndex = 9000;

                    if (location.isValidLocation && location.transportName === "Elit Klinik") {
                        size = 70;
                        zIndex = 9001;
                    }

                    return (
                        <AdvancedMarker
                            key={location.name}
                            position={location.isValidLocation ? location.coords : { lat: 0, lng: 0 }}
                            zIndex={zIndex}
                            // title={location.name}
                            onClick={() => {}}
                        >
                            <div className="hover:opacity-20 cursor-grab transition-all">
                                <img
                                    src={location.isValidLocation ? location.mapLogo : ""}
                                    alt={location.isValidLocation ? location.transportName : ""}
                                    style={{ width: size, height: size }}
                                />
                            </div>
                        </AdvancedMarker>
                    );
                })}

            {visibility.showCars &&
                vehicles?.map((vehicle) => {
                    const zIndex = 9010;

                    return (
                        <AdvancedMarker
                            key={vehicle.properties.id}
                            zIndex={zIndex}
                            position={{
                                lat: vehicle.geometry.coordinates[1],
                                lng: vehicle.geometry.coordinates[0],
                            }}
                            onClick={() => {
                                if (selectedVehicle === vehicle) setSelectedVehicle(undefined);
                                else setSelectedVehicle(vehicle);
                            }}
                        >
                            <div className="cursor-pointer" style={{ rotate: `${vehicle.properties.angle}deg` }}>
                                {vehicle.properties.speed > 0 ? (
                                    <img
                                        src={"map_icons/transport.svg"}
                                        alt={vehicle.properties.plate}
                                        style={{ width: `${20}px` }}
                                    />
                                ) : (
                                    <TbPointFilled className="text-elitDarkBlue/75" size={30} />
                                )}
                            </div>
                        </AdvancedMarker>
                    );
                })}

            {selectedVehicle && (
                <div className="bg-elitDarkBlue text-white bottom-24 md:bottom-2 mb-2 fixed rounded-3xl left-20 right-20 md:left-80 md:right-80 z-50 gap-0 p-1 px-4 flex justify-between">
                    <div className="flex justify-center items-center">
                        <IconButton
                            color="secondary"
                            disabled={!selectedVehicle}
                            size="small"
                            onClick={(e) => {
                                handleCenterVehicle(selectedVehicle);
                                e.stopPropagation();
                            }}
                        >
                            <LocationOn />
                        </IconButton>
                    </div>
                    <div className="flex flex-col justify-center items-center text-center">
                        <p className="m-0 overflow-ellipsis">{selectedVehicle.properties.driver_name}</p>
                        <p className="m-0 text-xs">{selectedVehicle.properties.speed}Km/h</p>
                    </div>
                    <div className="flex justify-center items-center">
                        <IconButton
                            color="secondary"
                            disabled={
                                !getDriverByVehicleId(selectedVehicle.properties.id.toString()) ||
                                !checkPermissions(userData?.permissionCodes, [PERMISSIONS.TR_SVC_DRIVERS_CAMERAS])
                            }
                            size="small"
                            onClick={(e) => {
                                const driver = getDriverByVehicleId(selectedVehicle.properties.id.toString());

                                if (driver) handleOpenCamerasDialog(driver);
                                e.stopPropagation();
                            }}
                        >
                            <VideoCameraBack />
                        </IconButton>
                    </div>
                </div>
            )}

            <div className="bg-elitDarkBlue text-white bottom-24 md:bottom-2 mb-2 fixed rounded-3xl left-auto right-4 z-50 gap-0 p-1 flex justify-between">
                <IconButton
                    color="secondary"
                    size="small"
                    onClick={(e) => {
                        handleGoToKlinik();
                        e.stopPropagation();
                    }}
                >
                    <ElitHairIcon />
                </IconButton>
            </div>
        </div>
    );
};

const TaskRoute = ({
    routeData,
}: // vehicles,
{
    routeData: RouteType;
    // vehicles: WeTrackVehicleData[];
}) => {
    const map = useMap();
    const routesLibrary = useMapsLibrary("routes");
    const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService>();
    const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer>();

    useEffect(() => {
        if (!routesLibrary || !map) return;

        setDirectionsService(new routesLibrary.DirectionsService());
        setDirectionsRenderer(new routesLibrary.DirectionsRenderer({ map }));
    }, [routesLibrary, map]);

    useEffect(() => {
        const { task, vehicle } = routeData;

        if (!directionsService || !directionsRenderer || !task) return;

        let origin = null;
        let destination = null;

        ALL_LOCATIONS.forEach((location) => {
            if (location.isValidLocation) {
                if (location.transportName === task.origin.alias) {
                    origin = location.coords;
                }
                if (location.transportName === task.destination.alias) {
                    destination = location.coords;
                }
            }
        });

        if (!origin || !destination) {
            return;
        }

        directionsService
            .route({
                unitSystem: google.maps.UnitSystem.METRIC,
                origin: origin,
                destination: destination,
                waypoints: [
                    {
                        location: {
                            lat: vehicle.geometry.coordinates[1],
                            lng: vehicle.geometry.coordinates[0],
                        },
                        stopover: true,
                    },
                ],
                travelMode: google.maps.TravelMode.DRIVING,
            })
            .then((response) => {
                directionsRenderer.setDirections(response);
                directionsRenderer.setOptions({
                    polylineOptions: { strokeColor: "#0059b2", zIndex: 9020, strokeWeight: 5 },
                    suppressMarkers: true,
                    preserveViewport: true,
                });
            });
    }, [directionsService, directionsRenderer, routeData]);

    return null;
};

export default MapPage;
